import { logger } from '@/helpers/logger';
import React, { ReactNode, useContext } from 'react';
import { PageContentItem } from '../../models/page';
import { CmsComponentsContext } from '../../page-templates/CmsComponentsContext';
import DevErrorMessage from '../DevErrorMessage/DevErrorMessage';
import {
  CONTENTS_AS_PROP_COMPONENT_WHITELIST,
  IGNORED_COMPONENT_NAMES,
} from './consts';
import type { ItemToComponentProps } from './interfaces';

const ItemToComponent = ({ item }: ItemToComponentProps) => {
  const cmsComponents = useContext(CmsComponentsContext);

  if (!cmsComponents) {
    throw new Error('invalid cms components');
  }

  if (isIgnoredComponent(item)) {
    return <React.Fragment />;
  }

  const isContents = isContentsComponent(item.componentName);
  const Component = isContents
    ? React.Fragment
    : cmsComponents[item.componentName];

  if (!Component) {
    logger.error(`No component with name: ${item.componentName}`);
    return (
      <DevErrorMessage>
        No component with name: {item.componentName}
      </DevErrorMessage>
    );
  }

  const { props, children, shouldIterateChildren } = processItemProps(item);

  const cleanProps =
    Component === React.Fragment || !props
      ? undefined
      : Object.fromEntries(
          Object.entries(props).filter(([key]) => !key.startsWith('jcr:'))
        );

  return (
    <Component {...cleanProps}>
      {shouldIterateChildren && Array.isArray(children)
        ? children.map((child) => (
            <ItemToComponent key={child.id} item={child} />
          ))
        : children}
    </Component>
  );
};

export default ItemToComponent;

function isIgnoredComponent(item: PageContentItem<any, any, any>) {
  return IGNORED_COMPONENT_NAMES.includes(item.componentName);
}

function isContentsComponent(componentName: string): boolean {
  return componentName.startsWith('contents-');
}

function isWhitelistedForContents(componentName: string): boolean {
  return CONTENTS_AS_PROP_COMPONENT_WHITELIST.some((value) =>
    typeof value === 'string'
      ? value === componentName
      : value.test(componentName)
  );
}

function partitionContents(contents: readonly PageContentItem[]) {
  const contentsComponents: Record<string, ReactNode> = {};
  const regularChildren: PageContentItem[] = [];

  contents.forEach((item) => {
    if (isContentsComponent(item.componentName)) {
      contentsComponents[item.componentName] = <ItemToComponent item={item} />;
    } else {
      regularChildren.push(item);
    }
  });

  return { contentsComponents, regularChildren };
}

function processItemProps(item: PageContentItem) {
  const baseProps = { ...item.props };

  if (!item.contents?.length) {
    return {
      props: baseProps,
      children: baseProps.children,
      shouldIterateChildren: false,
    };
  }

  const { contentsComponents, regularChildren } = partitionContents(
    item.contents
  );

  if (baseProps.children) {
    logger.warn(
      `Component ${item.componentName} has contents AND children. Something is wrong!`
    );
  }

  const processedProps = { ...baseProps, ...contentsComponents };

  if (isWhitelistedForContents(item.componentName)) {
    return {
      props: { ...processedProps, contents: item.contents },
      children: regularChildren,
      shouldIterateChildren: false,
    };
  }

  return {
    props: processedProps,
    children: regularChildren,
    shouldIterateChildren: true,
  };
}
